'use client';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useRouter } from 'next/navigation';
import { FaPlay, FaPause, FaYoutube, FaLinkedinIn } from 'react-icons/fa';
import { BiStore } from 'react-icons/bi';
import { useTranslation } from '@/hooks/useTranslation';
import LouiseButton from '@/components/buttons/LouiseButton';

interface PageContentProps {
  locale: string;
}

interface PackageData {
  version: string;
  price: number;
  name: string;
  type: string;
  data: {
    delivery_delay: number;
    promotional_video?: string;
    linkedin_link?: string;
    youtube_link?: string;
    image?: string;
    description?: string;
  };
  package_id: string;
  configuration: {
    bots: string[];
  };
  allTransactionCount: number;
  package_public_name: string;
  publisher: {
    name: string;
  };
}

interface ApiResponse {
  element: {
    packages: PackageData[];
  };
}

export default function PageContent({ locale }: PageContentProps) {
  const [packages, setPackages] = useState<PackageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [playingVideos, setPlayingVideos] = useState<Record<string, boolean>>({});
  const { t } = useTranslation(locale);
  const router = useRouter();

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get<ApiResponse>('/service/api/marketplace');

        if (response.data?.element?.packages) {
          setPackages(response.data.element.packages);
        } else {
          setError('Invalid response format');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load packages');
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // Scroll to package element if hash is present in URL
  useEffect(() => {
    if (!loading && packages.length > 0) {
      const hash = window.location.hash;
      if (hash) {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }
      }
    }
  }, [loading, packages]);

  if (loading) {
    return (
      <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-gray-50 tw-to-gray-100 dark:tw-from-gray-900 dark:tw-to-gray-800">
        <div className="tw-container tw-mx-auto tw-px-4 tw-py-16">
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-space-y-4">
            <div className="tw-w-16 tw-h-16 tw-border-4 tw-border-blue-600 tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
            <p className="tw-text-lg tw-text-gray-600 dark:tw-text-gray-300">{t('Marketplace/loading/text')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-gray-50 tw-to-gray-100 dark:tw-from-gray-900 dark:tw-to-gray-800">
        <div className="tw-container tw-mx-auto tw-px-4 tw-py-16">
          <div className="tw-max-w-md tw-mx-auto tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-800 tw-rounded-lg tw-p-6">
            <h3 className="tw-text-lg tw-font-semibold tw-text-red-800 dark:tw-text-red-200 tw-mb-2">
              {t('Marketplace/error/title')}
            </h3>
            <p className="tw-text-red-600 dark:tw-text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="tw-min-h-screen tw-bg-gradient-to-br tw-from-slate-50 tw-to-blue-50 tw-pb-8 md:tw-pb-16 plan-page  tw-font-['Lexend']">
      {/* Header Section */}
      <div className="tw-bg-gradient-to-r tw-from-blue-600 tw-to-purple-600 tw-text-white tw-py-16">
        <div className="tw-container tw-mx-auto tw-px-4">
          <h1 className="tw-text-5xl tw-font-bold tw-mb-4">{t('Marketplace/header/title')}</h1>
          <p className="tw-text-xl tw-text-blue-100 tw-max-w-2xl tw-mb-6">{t('Marketplace/header/description')}</p>
          <LouiseButton
            color="white"
            cta={t('Marketplace/header/cta')}
            ctaTarget="register"
            locale={locale}
            animation
          />
        </div>
      </div>

      {/* Packages Grid */}
      <div className="tw-container tw-mx-auto tw-px-4 tw-py-12 tw-max-w-7xl">
        {packages.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-gray-500 dark:text-gray-400">{t('Marketplace/error/noPackages')}</p>
          </div>
        ) : (
          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-6">
            {packages.map((pkg) => (
              <div
                key={pkg.package_id}
                id={`package-${pkg.package_id}`}
                className="tw-col-span-1"
                style={{ perspective: '1000px' }}
              >
                <div
                  className="tw-relative tw-w-full tw-h-full"
                  style={{
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.7s',
                    transform:
                      hoveredCard === pkg.package_id && pkg.data.promotional_video
                        ? 'rotateY(180deg)'
                        : 'rotateY(0deg)',
                    minHeight: '500px',
                  }}
                >
                  {/* Front Side - Package Card */}
                  <div
                    className="tw-group tw-block tw-absolute tw-inset-0 tw-cursor-pointer"
                    style={{ backfaceVisibility: 'hidden' }}
                    onClick={() => router.push(`/${locale}/marketplace/${pkg.name}`)}
                  >
                    <div className="tw-bg-white dark:tw-bg-gray-800 tw-rounded-lg tw-shadow-md tw-overflow-hidden tw-transition-all tw-duration-300 hover:tw-shadow-xl hover:tw--translate-y-1 tw-h-full tw-flex tw-flex-col">
                      {/* Package Image */}
                      <div
                        className="tw-relative tw-w-full tw-aspect-video tw-flex-shrink-0 tw-bg-gradient-to-br tw-from-blue-500 tw-to-purple-600 tw-overflow-hidden"
                        onMouseEnter={() => pkg.data.promotional_video && setHoveredCard(pkg.package_id)}
                      >
                        {pkg.data.image ? (
                          <img
                            src={pkg.data.image}
                            alt={pkg.name}
                            className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover tw-transition-transform tw-duration-300 group-hover:tw-scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="text-white text-5xl font-bold opacity-20">
                              {pkg.name.charAt(0).toUpperCase()}
                            </div>
                          </div>
                        )}
                        {/* Promotional Video Badge */}
                        {pkg.data.promotional_video && (
                          <div className="tw-absolute tw-top-2 tw-left-2 tw-bg-gradient-to-r tw-from-purple-600 tw-to-pink-600 tw-backdrop-blur-sm tw-px-2.5 tw-py-1.5 tw-rounded-lg tw-flex tw-items-center tw-gap-1.5 tw-shadow-lg">
                            <FaPlay className="tw-w-3 tw-h-3 tw-text-white" />
                            <span className="tw-text-xs tw-font-semibold tw-text-white">VideoBot</span>
                          </div>
                        )}
                      </div>

                      {/* Package Content */}
                      <div className="tw-p-4 tw-flex-1 tw-flex tw-flex-col">
                        {/* Title and Version */}
                        <div className="tw-flex tw-items-start tw-justify-between tw-mb-2">
                          <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white group-hover:tw-text-blue-600 dark:group-hover:tw-text-blue-400 tw-transition-colors tw-line-clamp-1">
                            {pkg.name}
                          </h3>
                          <span className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-bg-gray-100 dark:tw-bg-gray-700 tw-px-2 tw-py-1 tw-rounded tw-ml-2 tw-flex-shrink-0">
                            {t('Marketplace/card/version')}
                            {pkg.version}
                          </span>
                        </div>

                        {/* Description */}
                        {pkg.data.description && (
                          <div className="tw-mb-3 tw-text-sm tw-text-gray-600 dark:tw-text-gray-300 tw-line-clamp-2">
                            {pkg.data.description.replace(/[#*]/g, '').substring(0, 80) +
                              (pkg.data.description.length > 80 ? '...' : '')}
                          </div>
                        )}

                        {/* Bots Configuration */}
                        {pkg.configuration.bots.length > 0 && (
                          <div className="tw-mb-3">
                            <div className="tw-flex tw-flex-wrap tw-gap-1">
                              <span className="tw-text-xs tw-bg-blue-100 dark:tw-bg-blue-900/30 tw-text-blue-800 dark:tw-text-blue-200 tw-px-2 tw-py-1 tw-rounded">
                                {t('Marketplace/card/includeBots', { count: pkg.configuration.bots?.length })}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Spacer to push footer to bottom */}
                        <div className="tw-flex-1"></div>

                        {/* Footer */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-pt-3 tw-border-t tw-border-gray-200 dark:tw-border-gray-700 tw-mt-auto">
                          {/* Price */}
                          <div className="flex items-baseline">
                            <span className="tw-text-2xl tw-font-bold tw-text-gray-900 dark:tw-text-white">
                              ${pkg.price}
                            </span>
                          </div>

                          {/* Publisher & Social Links */}
                          <div className="tw-flex tw-items-center tw-gap-3">
                            {/* Social Links */}
                            {(pkg.data.youtube_link || pkg.data.linkedin_link) && (
                              <div className="tw-flex tw-items-center tw-gap-2">
                                {pkg.data.youtube_link && (
                                  <a
                                    href={pkg.data.youtube_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="tw-text-red-600 dark:tw-text-red-400 hover:tw-text-red-700 dark:hover:tw-text-red-300 tw-transition-colors"
                                    title="Watch on YouTube"
                                  >
                                    <FaYoutube className="tw-w-5 tw-h-5" />
                                  </a>
                                )}
                                {pkg.data.linkedin_link && (
                                  <a
                                    href={pkg.data.linkedin_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="tw-text-blue-700 dark:tw-text-blue-400 hover:tw-text-blue-800 dark:hover:tw-text-blue-300 tw-transition-colors"
                                    title="Connect on LinkedIn"
                                  >
                                    <FaLinkedinIn className="tw-w-5 tw-h-5" />
                                  </a>
                                )}
                              </div>
                            )}

                            {/* Publisher */}
                            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                              {/* Icon Logic: Louise icon for Louise, Store icon for marketplace */}
                              {pkg.publisher?.name && pkg.publisher?.name.toLowerCase() !== 'louise' ? (
                                <BiStore className="tw-w-5 tw-h-5" />
                              ) : (
                                <img
                                  src="https://cdn.1cdn.app/application/LOUISEBOT/23091707385795_Logo_colorful.svg"
                                  alt="Louise"
                                  className="tw-w-5 tw-h-5 tw-rounded-full"
                                />
                              )}

                              {/* Author Name */}
                              {pkg.publisher?.name && pkg.publisher?.name.toLowerCase() === 'louise' && (
                                <span className="tw-text-gray-600 tw-text-sm tw-font-medium">{pkg.publisher.name}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Call to Action */}
                        <div className="tw-mt-3">
                          <div className="tw-w-full tw-bg-gradient-to-r tw-from-blue-600 tw-to-purple-600 tw-text-white tw-text-center tw-py-2 tw-rounded tw-text-sm tw-font-semibold tw-transition-all tw-duration-300 group-hover:tw-shadow-lg">
                            {t('Marketplace/card/viewDetails')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Back Side - Video (Full Height) */}
                  {pkg.data.promotional_video && (
                    <div
                      className="tw-absolute tw-inset-0 tw-bg-black tw-rounded-lg tw-shadow-md tw-overflow-hidden"
                      style={{
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                      }}
                      onMouseLeave={() => {
                        setHoveredCard(null);
                        // Stop video when flipping back
                        const video = document.getElementById(`video-${pkg.package_id}`) as HTMLVideoElement;
                        if (video) {
                          video.pause();
                          video.currentTime = 0;
                        }
                      }}
                    >
                      <div className="tw-relative tw-w-full tw-h-full">
                        <video
                          id={`video-${pkg.package_id}`}
                          src={pkg.data.promotional_video}
                          className="tw-w-full tw-h-full tw-object-cover"
                          playsInline
                          onPlay={() => setPlayingVideos((prev) => ({ ...prev, [pkg.package_id]: true }))}
                          onPause={() => setPlayingVideos((prev) => ({ ...prev, [pkg.package_id]: false }))}
                        >
                          Your browser does not support the video tag.
                        </video>

                        {/* Play/Pause Button Overlay */}
                        <button
                          className="tw-absolute tw-top-1/2 tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-bg-black/60 hover:tw-bg-black/80 tw-text-white tw-rounded-full tw-p-6 tw-transition-all tw-duration-300 tw-z-10 hover:tw-scale-110"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const video = document.getElementById(`video-${pkg.package_id}`) as HTMLVideoElement;
                            if (video) {
                              if (video.paused) {
                                video.play();
                              } else {
                                video.pause();
                              }
                            }
                          }}
                        >
                          {playingVideos[pkg.package_id] ? (
                            <FaPause className="tw-w-8 tw-h-8" />
                          ) : (
                            <FaPlay className="tw-w-8 tw-h-8 tw-ml-1" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
